import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useDeleteDayOff } from "../../../../pages/Profile/v2/hooks";
import type { DayOff, PtoRef, PunchSummary, PunchSummaryCategory } from "../../../../utils/models";
import { nursePreferencesToDaysOffAndPto } from "../../../../utils/utilFunctions";
import { useDescribeBuckets } from "./describeBuckets";

const NOTIFY_CATEGORIES = new Set<PunchSummaryCategory>(["noPunch", "missingPunch", "shiftDelta", "callIn", "agency"]);

export type PunchActionRow = {
	nurseId: number;
	name: string;
	subLine?: string;
	preference?: PtoRef;
	shiftDelta?: { scheduledHours: number; actualHours: number; diffHours: number };
	missingPunch?: boolean;
};

export const buildRows = (category: PunchSummaryCategory, summary: PunchSummary, t: TFunction): PunchActionRow[] => {
	switch (category) {
		case "noPunch":
			return summary.noPunch.map((e) => ({ nurseId: e.nurseId, name: e.name }));
		case "missingPunch":
			return summary.missingPunch.map((e) => ({ nurseId: e.nurseId, name: e.name }));
		case "notScheduled":
			return summary.notScheduled.employees.map((e) => ({
				nurseId: e.nurseId,
				name: e.name,
				subLine: `${e.actualHours.toFixed(1)}h actual`,
			}));
		case "excludedFromPpd":
			return summary.excludedFromPpd.employees.map((e) => ({
				nurseId: e.nurseId,
				name: e.name,
				subLine: `${e.actualHours.toFixed(1)}h actual`,
			}));
		case "shiftDelta":
			return summary.shiftDelta.employees.map((e) => ({
				nurseId: e.nurseId,
				name: e.name,
				shiftDelta: { scheduledHours: e.scheduledHours, actualHours: e.actualHours, diffHours: e.diffHours },
			}));
		case "ot":
			return summary.ot.employees.map((e) => ({ nurseId: e.nurseId, name: e.name, subLine: `+${e.otHours.toFixed(1)}h OT` }));
		case "agency":
			return summary.agency.employees.map((e) => ({
				nurseId: e.nurseId,
				name: e.name,
				subLine: [e.agencyName, e.missingPunch ? t("punchSummary.category.missingPunch") : `${e.agencyHours.toFixed(1)}h`].filter(Boolean).join(" · "),
				missingPunch: e.missingPunch,
			}));
		case "callIn":
			return summary.callIn.employees.map((e) => ({ nurseId: e.nurseId, name: e.name, subLine: e.reason }));
		case "pto":
			return summary.pto.map((p) => ({ nurseId: p.nurseId, name: p.name, subLine: p.reason ?? "", preference: p }));
	}
};

type UsePunchActionCenterInput = {
	activeDay: DateTime | null;
	activeCategory: PunchSummaryCategory | null;
	summary: PunchSummary | null;
};

export const usePunchActionCenter = ({ activeDay, activeCategory, summary }: UsePunchActionCenterInput) => {
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const { chosenDepartment, getNurseById, updateNurse, addNewDBNurseShifts, removeNursesShiftsCompletely } = useHospitalData();
	const { deleteDayOffMutateAsync } = useDeleteDayOff({ addNewDBNurseShifts, removeNursesShiftsCompletely });

	const [showNotifyAll, setShowNotifyAll] = useState<boolean>(false);
	const [notifyAllNurseIds, setNotifyAllNurseIds] = useState<number[] | null>(null);
	const [notifyForNurse, setNotifyForNurse] = useState<{ nurseId: number; name: string } | null>(null);
	const [ptoMenuAnchor, setPtoMenuAnchor] = useState<{ anchor: HTMLElement; preference: PtoRef } | null>(null);
	const [editingDayOff, setEditingDayOff] = useState<{ nurseId: number; dayOffId: string } | null>(null);
	const [ptoToDelete, setPtoToDelete] = useState<{ ptoRef: PtoRef } | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const rows = useMemo<PunchActionRow[]>(() => {
		if (!summary || !activeCategory) return [];
		return buildRows(activeCategory, summary, t).sort((a, b) => a.name.localeCompare(b.name));
	}, [summary, activeCategory, t]);

	const describeBucket = useDescribeBuckets();
	const description = useMemo(() => {
		if (!summary || !activeCategory) return null;
		return describeBucket(summary, activeCategory);
	}, [summary, activeCategory, describeBucket]);

	const resolveDayOff = useCallback(
		(nurseId: number, dayOffId: string) => {
			const nurse = getNurseById(nurseId);
			if (!nurse) return null;
			return nursePreferencesToDaysOffAndPto(nurse).find((d) => d.id === dayOffId) ?? null;
		},
		[getNurseById],
	);

	const editingDayOffData = useMemo(() => {
		if (!editingDayOff) return null;
		const dayOff = resolveDayOff(editingDayOff.nurseId, editingDayOff.dayOffId);
		return dayOff ? { dayOff, nurseId: editingDayOff.nurseId } : null;
	}, [editingDayOff, resolveDayOff]);

	const deleteDayOffData = useMemo(() => {
		if (!ptoToDelete) return null;
		return resolveDayOff(ptoToDelete.ptoRef.nurseId, ptoToDelete.ptoRef.preferenceId);
	}, [ptoToDelete, resolveDayOff]);

	const deletePtoNurseId = ptoToDelete?.ptoRef.nurseId ?? null;

	const isOpen = !!activeDay && !!activeCategory && !!summary && !!description;
	const showNotifyButton = !!activeCategory && NOTIFY_CATEGORIES.has(activeCategory) && (activeCategory !== "agency" || rows.some((r) => r.missingPunch));

	// Agency staff are only ever messaged about a missing punch, so "notify all" for that category
	// reaches a subset of the rows on screen. The panel names this count, so it can't be derived
	// inside the click handler alone.
	const notifyAllTargetRows = useMemo<PunchActionRow[]>(
		() => (activeCategory === "agency" ? rows.filter((r) => r.missingPunch) : rows),
		[activeCategory, rows],
	);

	const handleNotifyAll = useCallback((): void => {
		if (!activeCategory) return;
		track("punch_summary_open_notify_all", { category: activeCategory, department_id: chosenDepartment.department_id, count: notifyAllTargetRows.length });
		setNotifyAllNurseIds(notifyAllTargetRows.map((r) => r.nurseId));
		setShowNotifyAll(true);
	}, [activeCategory, chosenDepartment.department_id, notifyAllTargetRows, track]);

	const handleCloseNotifyAll = useCallback(() => {
		setShowNotifyAll(false);
		setNotifyAllNurseIds(null);
	}, []);

	const handleNotifyOne = useCallback(
		(row: PunchActionRow): void => {
			if (!activeCategory) return;
			track("punch_summary_open_notify", { category: activeCategory, department_id: chosenDepartment.department_id, nurse_id: row.nurseId });
			setNotifyForNurse({ nurseId: row.nurseId, name: row.name });
		},
		[activeCategory, chosenDepartment.department_id, track],
	);

	const handleCloseNotifyOne = useCallback(() => setNotifyForNurse(null), []);

	const handleEditPtoRow = useCallback(
		(preference: PtoRef) => {
			track("punch_summary_edit_pto", { preference_id: preference.preferenceId, nurse_id: preference.nurseId });
			setEditingDayOff({ nurseId: preference.nurseId, dayOffId: preference.preferenceId });
		},
		[track],
	);

	const handleDeletePtoRow = useCallback(
		(preference: PtoRef) => {
			track("punch_summary_delete_pto", { preference_id: preference.preferenceId, nurse_id: preference.nurseId });
			setPtoToDelete({ ptoRef: preference });
		},
		[track],
	);

	const handleOpenPtoMenu = useCallback((anchor: HTMLElement, preference: PtoRef) => {
		setPtoMenuAnchor({ anchor, preference });
	}, []);

	const handleClosePtoMenu = useCallback(() => setPtoMenuAnchor(null), []);

	const handleEditPtoFromMenu = useCallback(
		(preferenceId: string, nurseId: number) => {
			track("punch_summary_edit_pto", { preference_id: preferenceId, nurse_id: nurseId });
			setEditingDayOff({ nurseId, dayOffId: preferenceId });
			setPtoMenuAnchor(null);
		},
		[track],
	);

	const handleDeletePtoFromMenu = useCallback(
		(preferenceId: string, nurseId: number) => {
			track("punch_summary_delete_pto", { preference_id: preferenceId, nurse_id: nurseId });
			setPtoMenuAnchor((current) => {
				if (current) setPtoToDelete({ ptoRef: current.preference });
				return null;
			});
		},
		[track],
	);

	const handleCloseEditingDayOff = useCallback(() => setEditingDayOff(null), []);

	const handleSaveEditingDayOff = useCallback(
		(updated: DayOff) => {
			if (!editingDayOffData) {
				setEditingDayOff(null);
				return;
			}
			const nurse = getNurseById(editingDayOffData.nurseId);
			if (!nurse) {
				setEditingDayOff(null);
				return;
			}
			updateNurse({
				...nurse,
				employeeSpecificPreferences: nurse.employeeSpecificPreferences.map((pref) =>
					pref.id === updated.id ? { ...pref, start_date: updated.dateFrom, end_date: updated.dateTo, hours: updated.hours } : pref,
				),
			});
			setEditingDayOff(null);
		},
		[editingDayOffData, getNurseById, updateNurse],
	);

	const handleCancelPtoDelete = useCallback(() => setPtoToDelete(null), []);

	const handleConfirmDeletePto = useCallback(
		async (restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean): Promise<void> => {
			if (!ptoToDelete) return;
			const nurse = getNurseById(ptoToDelete.ptoRef.nurseId);
			if (!nurse) {
				setPtoToDelete(null);
				return;
			}
			const dayOff = resolveDayOff(ptoToDelete.ptoRef.nurseId, ptoToDelete.ptoRef.preferenceId);
			if (!dayOff) {
				setPtoToDelete(null);
				return;
			}
			setIsDeleting(true);
			try {
				await deleteDayOffMutateAsync({
					dayOff,
					nurseId: nurse.id,
					departmentId: chosenDepartment.department_id,
					restoreRecurringShifts,
					restoreTrimmedShifts,
				});
				updateNurse({
					...nurse,
					employeeSpecificPreferences: nurse.employeeSpecificPreferences.filter((pref) => pref.id !== ptoToDelete.ptoRef.preferenceId),
				});
			} finally {
				setIsDeleting(false);
				setPtoToDelete(null);
			}
		},
		[chosenDepartment.department_id, deleteDayOffMutateAsync, getNurseById, ptoToDelete, resolveDayOff, updateNurse],
	);

	const ptoMenuPreference = useMemo(() => {
		if (!ptoMenuAnchor) return null;
		const nurse = getNurseById(ptoMenuAnchor.preference.nurseId);
		const pref = nurse?.employeeSpecificPreferences.find((p) => p.id === ptoMenuAnchor.preference.preferenceId);
		return pref ? { pref, anchor: ptoMenuAnchor.anchor, nurseId: ptoMenuAnchor.preference.nurseId } : null;
	}, [getNurseById, ptoMenuAnchor]);

	return {
		// derived
		isOpen,
		rows,
		description,
		showNotifyButton,
		chosenDepartment,
		// notify all
		notifyAllRecipientCount: notifyAllTargetRows.length,
		showNotifyAll,
		notifyAllNurseIds,
		handleNotifyAll,
		handleCloseNotifyAll,
		// notify one
		notifyForNurse,
		handleNotifyOne,
		handleCloseNotifyOne,
		// pto row actions
		handleEditPtoRow,
		handleDeletePtoRow,
		handleOpenPtoMenu,
		// pto menu
		ptoMenuPreference,
		handleClosePtoMenu,
		handleEditPtoFromMenu,
		handleDeletePtoFromMenu,
		// edit dialog
		editingDayOffData,
		handleCloseEditingDayOff,
		handleSaveEditingDayOff,
		// delete dialog
		deleteDayOffData,
		deletePtoNurseId,
		isDeleting,
		handleCancelPtoDelete,
		handleConfirmDeletePto,
	};
};
